'use client';

import React, { useState, useEffect } from 'react';
import { X, Plus, BookMarked, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Collection {
  id: string;
  name: string;
  item_count: number;
  emoji: string;
}

interface CollectionsSidebarProps {
  open: boolean;
  onClose: () => void;
  collections: Collection[];
  onCollectionsChange?: (collections: Collection[]) => void;
}

export default function CollectionsSidebar({
  open,
  onClose,
  collections,
  onCollectionsChange,
}: CollectionsSidebarProps) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmoji, setNewEmoji] = useState('📚');
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const supabase = createClient();

  const emojiOptions = ['📚', '⭐', '🗺️', '💎', '🏛️', '🥃', '⌚', '🚗', '✈️', '🎨', '👑', '🌿'];

  const handleCreateCollection = async () => {
    if (newName.trim().length < 2 || !user) return;
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('collections')
        .insert({ user_id: user.id, name: newName.trim(), emoji: newEmoji })
        .select()
        .single();
      if (error) throw error;
      const updated = [...collections, { id: data.id, name: data.name, item_count: 0, emoji: data.emoji }];
      onCollectionsChange?.(updated);
      toast.success(`Collection "${newName}" created`);
      setNewName('');
      setNewEmoji('📚');
      setCreating(false);
    } catch {
      toast.error('Failed to create collection');
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-obsidian/70 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-80 xl:w-96 glass-panel border-l border-muted z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-muted">
          <div className="flex items-center gap-2">
            <BookMarked size={16} className="text-gold" />
            <span className="font-editorial text-base font-semibold text-cream">
              My Collections
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close collections panel"
            className="text-muted-foreground hover:text-cream transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Stats */}
        <div className="px-6 py-4 border-b border-muted grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Boards</p>
            <p className="font-editorial text-2xl font-bold text-gold text-tabular">{collections.length}</p>
          </div>
          <div>
            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Saved Items</p>
            <p className="font-editorial text-2xl font-bold text-gold text-tabular">
              {collections.reduce((sum, c) => sum + (c.item_count || 0), 0)}
            </p>
          </div>
        </div>

        {/* Collections List */}
        <div className="flex-1 overflow-y-auto py-4 px-4 space-y-2 scroll-hidden">
          {collections.length === 0 ? (
            <div className="text-center py-8">
              <BookMarked size={24} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No collections yet</p>
              <p className="text-xs text-muted-foreground mt-1">Create your first collection below</p>
            </div>
          ) : (
            collections.map((col) => (
              <button
                key={col.id}
                className="w-full flex items-center justify-between px-4 py-3 rounded-sm border border-muted hover:border-gold/30 hover:bg-muted/30 transition-all duration-200 group text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{col.emoji}</span>
                  <div>
                    <p className="text-sm font-medium text-cream group-hover:text-gold transition-colors">
                      {col.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {col.item_count} {col.item_count === 1 ? 'discovery' : 'discoveries'}
                    </p>
                  </div>
                </div>
                <ChevronRight size={14} className="text-muted-foreground group-hover:text-gold transition-colors" />
              </button>
            ))
          )}
        </div>

        {/* Create New Collection */}
        <div className="px-6 py-5 border-t border-muted">
          {creating ? (
            <div className="space-y-3">
              <label className="text-xs tracking-widest uppercase text-muted-foreground">
                Collection Name
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Rare Manuscripts"
                className="input-luxury w-full px-3 py-2.5 rounded-sm text-sm"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreateCollection()}
              />
              <div>
                <p className="text-xs tracking-widest uppercase text-muted-foreground mb-2">Choose Icon</p>
                <div className="flex flex-wrap gap-1.5">
                  {emojiOptions.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => setNewEmoji(emoji)}
                      className={`w-8 h-8 rounded text-base flex items-center justify-center transition-colors ${newEmoji === emoji ? 'bg-gold/20 border border-gold/50' : 'hover:bg-muted/30'}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateCollection}
                  disabled={newName.trim().length < 2 || saving}
                  className="flex-1 py-2 gold-gradient-bg text-obsidian text-xs font-semibold tracking-widest uppercase rounded-sm disabled:opacity-40 transition-opacity flex items-center justify-center gap-1.5"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                  Create
                </button>
                <button
                  onClick={() => { setCreating(false); setNewName(''); setNewEmoji('📚'); }}
                  className="flex-1 py-2 border border-muted text-muted-foreground text-xs tracking-widest uppercase rounded-sm hover:text-cream transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setCreating(true)}
              className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-muted text-muted-foreground hover:border-gold/40 hover:text-gold transition-all duration-200 rounded-sm text-sm"
            >
              <Plus size={15} />
              <span className="tracking-wide">New Collection</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}